<script lang="ts">
  import type { IStatWidget } from '../../modules/statwidget/statwidget.interface';
  import type {
    UserCountQueryResponse,
    DataItem,
    HistogramQuery,
    HistogramResponse,
    PopularResponse,
    WidgetQuery,
  } from '../../lib/pandalytics';
  import { IStatWidgetType } from '../../modules/statwidget/statwidget.interface';
  import {
    queryHistogram,
    queryPopular,
    queryActiveUser,
    queryUserCount,
  } from '../../lib/pandalytics';
  import Highchart from './Highchart.svelte';
  import ListView from './ListView.svelte';
  import Piechart from './Piechart.svelte';
  import { Select, ProgressCircular } from 'svelte-materialify';
  import { prepareData } from '../../modules/statwidget/statwidget.serializer';
  import dayjs from 'dayjs';
  import { total } from '../../store';
  import { createEventDispatcher } from 'svelte';

  const dispatch = createEventDispatcher();

  export let widget: IStatWidget;

  interface ISelectField {
    value: string;
    name: string;
  }

  const eventList: ISelectField[] = [
    { name: 'App Init', value: 'APP_INIT' },
    { name: 'Bookmark', value: 'BOOKMARK_ADD' },
    { name: 'Item Click', value: 'ITEM_CLICK' },
    { name: 'Scroll', value: 'SCROLL_PERCENTAGE' },
  ];

  export let selectedEvent = eventList[0].value;

  const dateRanges: ISelectField[] = [
    {
      value: '1d',
      name: '24 Hours',
    },
    {
      value: '7d',
      name: '7 Days',
    },
    {
      value: '1M',
      name: '1 Month',
    },
    {
      value: '3M',
      name: '3 Months',
    },
    {
      value: '1y',
      name: '1 Year',
    },
  ];

  const dateRangesWeekly: ISelectField[] = [
    {
      value: '7',
      name: '1 Week Ago',
    },
    {
      value: '14',
      name: '2 Week Ago',
    },
    {
      value: '21',
      name: '3 Week Ago',
    },
    {
      value: '28',
      name: '4 Week Ago',
    },
    {
      value: '35',
      name: '5 Week Ago',
    },
  ];

  const categoryList: ISelectField[] = [
    {
      value: 'all',
      name: 'All',
    },
    {
      value: 'portfolio',
      name: 'Portfolio',
    },
    {
      value: 'palette',
      name: 'Palette',
    },
    {
      value: 'link',
      name: 'Link',
    },
    {
      value: 'branding',
      name: 'Branding',
    },
    {
      value: 'font',
      name: 'Font',
    },
    {
      value: 'product',
      name: 'Product',
    },
    {
      value: 'podcast',
      name: 'Podcast',
    },
    {
      value: 'job',
      name: 'Job',
    },
    {
      value: 'instagram',
      name: 'Instagram',
    },
    {
      value: 'discussion',
      name: 'Discussion',
    },
    {
      value: 'dribbble',
      name: 'Dribbble',
    },
    {
      value: 'behance',
      name: 'Behance',
    },
    {
      value: 'article',
      name: 'Article',
    },
    {
      value: 'featured',
      name: 'Featured',
    },
    {
      value: 'nft',
      name: 'NFT',
    },
    {
      value: 'tweet',
      name: 'Tweet',
    },
    {
      value: 'figma',
      name: 'Figma',
    },
  ];

  let dataPromise: Promise<HistogramResponse | PopularResponse | UserCountQueryResponse>;
  let dateRange: string =
    widget.type !== 'chart' ? dateRanges[4].value : dateRanges[0].value;
  let dateRangeWeekly = parseInt(dateRangesWeekly[0].value);
  let query: WidgetQuery;
  let defaultCategory: string = categoryList[0].value;

  function getMondayOfCurrentWeek(dayAgo: number = 0) {
    const today = new Date();
    const first = today.getDate() - dayAgo - today.getDay() + 1;

    const monday = new Date(today.setDate(first));
    return monday;
  }

  $: query = {
    ...widget.query,
    endDate: dayjs().toDate(),
    startDate: dayjs()
      .subtract(...getStartDate(dateRange))
      .startOf('day')
      .toDate(),
    category: defaultCategory,
  };

  $: queryWeekly = {
    ...widget.query,
    endDate: dayjs(getMondayOfCurrentWeek(dateRangeWeekly - 7))
      .startOf('day')
      .toDate(),
    startDate: dayjs(getMondayOfCurrentWeek(dateRangeWeekly)).toDate(),
    category: categoryList[6].value,
  };

  $: userQuery = {
    ...widget.query,
    endDate: dayjs().toDate(),
    startDate: dayjs()
      .subtract(...getStartDate(dateRange))
      .startOf('day')
      .toDate(),
    category: defaultCategory,
  };
  $: onQueryChange(query || queryWeekly || userQuery);

  async function onQueryChange(query: WidgetQuery) {
    if (widget.type === IStatWidgetType.Table) {
      dataPromise = queryPopular(query).then(transform);
      if (widget.title === 'Most clicked product urls weekly')
        dataPromise = queryPopular(queryWeekly).then(transform);
    } else if (widget.type === IStatWidgetType.Chart) {
      if (widget.title === 'Registered and guest users')
        dataPromise = queryUserCount(userQuery).then(transform);
      else {
        dataPromise = queryActiveUser(userQuery).then(transform);
      }
    } else {
      dataPromise = queryHistogram(query as HistogramQuery).then(transform);
    }
  }

  function getStartDate(dateRange: string): [number, dayjs.OpUnitType] {
    const value = parseInt(dateRange[0]);
    const unit = dateRange[1] as dayjs.OpUnitType;

    return [value, unit];
  }

  async function transform(
    response: HistogramResponse | PopularResponse | UserCountQueryResponse
  ) {
    if (!widget.transformResponse) {
      return Promise.resolve(response);
    }

    return widget.transformResponse(response);
  }

  function onListItemClick(evt: { detail: DataItem }) {
    const dataItem = evt.detail;

    if (!widget.onClick) return;

    widget.onClick(dataItem);
  }
</script>

<div class="stat-widget">
  <div class="sw-header">
    <h3 class="sw-title">
      {widget.title}
      {#if widget.title === 'App Init'}
        <span class="label">
          {$total}
        </span>
      {/if}
    </h3>
    {#if widget.title === 'App Init' || widget.title === 'Bookmark' || widget.title === 'Item Click'}
      <Select
        items={eventList}
        on:change={() => dispatch('select-event', selectedEvent)}
        bind:value={selectedEvent}
      />
    {/if}
    {#if widget.title === 'Most clicked urls'}
      <Select items={categoryList} bind:value={defaultCategory} />
    {/if}
    <!-- svelte-ignore empty-block -->
    {#if widget.title === 'Most clicked product urls weekly'}
      <Select items={dateRangesWeekly} bind:value={dateRangeWeekly} />
    {:else if widget.title === 'Registered and guest users'}{:else}
      <Select items={dateRanges} bind:value={dateRange} />
    {/if}
  </div>
  <div class="sw-chart {widget.type}">
    {#await dataPromise}
      <div class="d-flex justify-center align-center" style="height: 80%;">
        <ProgressCircular size={50} indeterminate color="grey lighten-1" />
      </div>
    {:then response}
      {#if widget.type === 'bars' || widget.type === 'lines'}
        <Highchart data={prepareData(widget, response)} />
      {:else if widget.type === 'chart'}
        <Piechart countData={response} />
      {:else}
        <ListView
          weekly={widget.title}
          popularResponse={response}
          on:click={onListItemClick}
        />
      {/if}
    {:catch error}
      <p>{error.message | error}</p>
    {/await}
  </div>
</div>

<style lang="scss">
  .stat-widget {
    padding: 24px;
    width: 100%;
    padding-bottom: 0;
    background-color: #f7f7f8;
    border-radius: 10px;
    overflow-y: scroll;
  }

  .sw-header {
    padding: 0 8px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;

    :global(.s-select) {
      flex: 0 0 100px;
      transform: scale(0.8);
      z-index: 1;
    }
  }

  .sw-title {
    font-size: 18px;
    line-height: 21px;
    flex: 1;
    .label {
      padding: 6px 12px;
      color: #fff;
      background-color: #4067cc;
      font-weight: 700;
      font-size: 18px;
      line-height: 18px;
      border-radius: 9px;
    }
  }

  .sw-chart {
    height: 300px;

    &.table {
      height: 496px;
    }

    &.chart {
      height: 496px;
    }
  }
</style>
